import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Mail,
  Clock,
  CheckCircle,
  Target,
  MessageSquare,
  Lightbulb,
  Users,
  Settings,
  Bell,
  Calendar,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { toast } from "sonner";
import { notificationsService } from "@/services/notificationsService";

interface EmailPreferences {
  id?: string;
  user_id: string;
  email_notifications: boolean;
  email_frequency: 'instant' | 'daily' | 'weekly' | 'never';
  digest_enabled: boolean;
  check_in_notifications: boolean;
  goal_notifications: boolean;
  insight_notifications: boolean;
  team_notifications: boolean;
  chat_notifications: boolean;
}

export default function EmailNotificationPreferences() {
  const { user } = useUser();
  const [preferences, setPreferences] = useState<EmailPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const notificationTypes = [
    {
      key: 'check_in_notifications' as keyof EmailPreferences,
      title: 'Check-in Reminders',
      description: 'Daily and weekly reminders to complete your check-ins',
      icon: <CheckCircle className="h-5 w-5" />,
      color: 'text-blue-600',
      examples: ['Daily check-in reminder', 'Weekly check-in summary'],
    },
    {
      key: 'goal_notifications' as keyof EmailPreferences,
      title: 'Goal Alerts',
      description: 'Updates about your goals and progress reminders',
      icon: <Target className="h-5 w-5" />,
      color: 'text-green-600',
      examples: ['Goal due date reminders', 'Progress update requests', 'Goal completion celebrations'],
    },
    {
      key: 'chat_notifications' as keyof EmailPreferences,
      title: 'Messages & Mentions',
      description: 'When someone mentions you or sends you a message',
      icon: <MessageSquare className="h-5 w-5" />,
      color: 'text-purple-600',
      examples: ['Direct messages', '@username mentions', 'Team chat notifications'],
    },
    {
      key: 'insight_notifications' as keyof EmailPreferences,
      title: 'AI Insights',
      description: 'Personalized insights and growth recommendations',
      icon: <Lightbulb className="h-5 w-5" />,
      color: 'text-amber-600',
      examples: ['Weekly progress insights', 'Trend analysis', 'Personalized tips'],
    },
    {
      key: 'team_notifications' as keyof EmailPreferences,
      title: 'Team Updates',
      description: 'Team announcements and collaboration updates',
      icon: <Users className="h-5 w-5" />,
      color: 'text-indigo-600',
      examples: ['Team announcements', 'Shared goal updates', 'Team health alerts'],
    },
  ];

  const frequencyOptions = [
    { 
      value: 'instant', 
      label: 'Instant', 
      description: 'Immediately when they occur',
      icon: <Bell className="h-4 w-4" />
    },
    { 
      value: 'daily', 
      label: 'Daily Digest', 
      description: 'Once per day summary at 9 AM',
      icon: <Calendar className="h-4 w-4" />
    },
    { 
      value: 'weekly', 
      label: 'Weekly Summary', 
      description: 'Monday mornings with weekly stats',
      icon: <Clock className="h-4 w-4" />
    },
    { 
      value: 'never', 
      label: 'Never', 
      description: 'No email notifications',
      icon: <VolumeX className="h-4 w-4" />
    },
  ];

  useEffect(() => {
    if (user?.id) {
      loadPreferences();
    }
  }, [user?.id]);

  const loadPreferences = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const data = await notificationsService.getPreferences();

      // API returns data directly in camelCase - convert to snake_case for UI
      if (data && Object.keys(data).length > 0) {
        const convertedPrefs: EmailPreferences = {
          id: data.id,
          user_id: data.userId || user.id,
          email_notifications: data.emailNotifications ?? true,
          email_frequency: data.emailFrequency || 'daily',
          digest_enabled: data.digestEnabled ?? true,
          check_in_notifications: data.checkInNotifications ?? true,
          goal_notifications: data.goalNotifications ?? true,
          insight_notifications: data.insightNotifications ?? true,
          team_notifications: data.teamNotifications ?? true,
          chat_notifications: data.chatNotifications ?? false,
        };
        setPreferences(convertedPrefs);
      } else {
        // Create default preferences
        await createDefaultPreferences();
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
      toast.error('Failed to load notification preferences');
    } finally {
      setIsLoading(false);
    }
  };

  const createDefaultPreferences = async () => {
    if (!user?.id) return;

    try {
      const defaultPrefs: EmailPreferences = {
        user_id: user.id,
        email_notifications: true,
        email_frequency: 'daily',
        digest_enabled: true,
        check_in_notifications: true,
        goal_notifications: true,
        insight_notifications: true,
        team_notifications: true,
        chat_notifications: false, // More conservative default for chat
      };

      // Map to API format (camelCase)
      const apiPrefs = {
        emailNotifications: defaultPrefs.email_notifications,
        emailFrequency: defaultPrefs.email_frequency,
        digestEnabled: defaultPrefs.digest_enabled,
        checkInNotifications: defaultPrefs.check_in_notifications,
        goalNotifications: defaultPrefs.goal_notifications,
        insightNotifications: defaultPrefs.insight_notifications,
        teamNotifications: defaultPrefs.team_notifications,
        chatNotifications: defaultPrefs.chat_notifications,
      };

      await notificationsService.updatePreferences(apiPrefs);

      // Set local state directly with snake_case format
      setPreferences(defaultPrefs);
    } catch (error) {
      console.error('Failed to create default preferences:', error);
    }
  };

  const updatePreference = async (key: keyof EmailPreferences, value: boolean | string) => {
    if (!preferences) return;

    setIsSaving(true);
    try {
      const updatedPrefs = { ...preferences, [key]: value };
      
      // Map to API format (camelCase)
      const apiKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      const updates = { [apiKey]: value };
      
      await notificationsService.updatePreferences(updates);

      // API returns data directly - if no error thrown, update was successful
      setPreferences(updatedPrefs);
      toast.success('Preferences updated');
    } catch (error) {
      console.error('Failed to update preference:', error);
      toast.error('Failed to update preference');
    } finally {
      setIsSaving(false);
    }
  };


  if (isLoading) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle>Email Notification Preferences</CardTitle>
          <CardDescription>Loading your email preferences...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!preferences) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle>Email Notification Preferences</CardTitle>
          <CardDescription>Unable to load preferences</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={loadPreferences}>Retry</Button>
        </CardContent>
      </Card>
    );
  }

  const getStatusBadge = () => {
    if (!preferences.email_notifications) {
      return <Badge variant="secondary" className="bg-red-100 text-red-700">All Email Disabled</Badge>;
    }
    if (preferences.email_frequency === 'never') {
      return <Badge variant="secondary" className="bg-red-100 text-red-700">No Emails</Badge>;
    }
    if (preferences.email_frequency === 'instant') {
      return <Badge variant="default" className="bg-green-100 text-green-700">Instant Emails</Badge>;
    }
    return <Badge variant="secondary" className="bg-blue-100 text-blue-700">
      {preferences.email_frequency} Emails
    </Badge>;
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Notification Preferences
            </CardTitle>
            <CardDescription>
              Control when and how you receive email notifications from Step
            </CardDescription>
          </div>
          {getStatusBadge()}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Master Email Controls */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Email Settings
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center space-x-3">
                <Volume2 className="h-5 w-5 text-blue-600" />
                <div>
                  <Label htmlFor="email-notifications" className="text-base font-medium">
                    Enable Email Notifications
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Master switch for all email notifications
                  </p>
                </div>
              </div>
              <Switch
                id="email-notifications"
                checked={preferences.email_notifications}
                onCheckedChange={(checked) => updatePreference('email_notifications', checked)}
                disabled={isSaving}
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-green-600" />
                <div>
                  <Label htmlFor="digest-enabled" className="text-base font-medium">
                    Email Digest
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Receive summary emails with multiple notifications
                  </p>
                </div>
              </div>
              <Switch
                id="digest-enabled"
                checked={preferences.digest_enabled}
                onCheckedChange={(checked) => updatePreference('digest_enabled', checked)}
                disabled={isSaving || !preferences.email_notifications}
              />
            </div>
          </div>

          {preferences.email_notifications && (
            <div className="space-y-3">
              <Label className="text-base font-medium">Email Frequency</Label>
              <Select
                value={preferences.email_frequency}
                onValueChange={(value) => updatePreference('email_frequency', value)}
                disabled={isSaving}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {frequencyOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        {option.icon}
                        <div>
                          <div className="font-medium">{option.label}</div>
                          <div className="text-xs text-muted-foreground">{option.description}</div>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <Separator />

        {/* Notification Type Controls */}
        {preferences.email_notifications && preferences.email_frequency !== 'never' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Notification Types</h3>
            <p className="text-sm text-muted-foreground">
              Choose which types of notifications you want to receive via email
            </p>
            
            <div className="space-y-4">
              {notificationTypes.map((type) => (
                <div key={type.key} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className={`${type.color} mt-1`}>
                        {type.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Label className="text-base font-medium">
                            {type.title}
                          </Label>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {type.description}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {type.examples.map((example, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {example}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={preferences[type.key] as boolean ?? true}
                      onCheckedChange={(checked) => updatePreference(type.key, checked)}
                      disabled={isSaving}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}


        {isSaving && (
          <div className="text-center text-sm text-muted-foreground">
            Updating preferences...
          </div>
        )}
      </CardContent>
    </Card>
  );
}